import {Component} from "../../../../../../../sedestral/framework/interface/component/Component";
import {SedestralMachine} from "../../../../../../../sedestral/framework/machine/SedestralMachine";
import {SedestralInterface} from "../../../../../../../sedestral/framework/interface/SedestralInterface";
import {CommunityAgentScope} from "../types/CommunityAgentScope";
import {AgentAvatarComponent} from "./AgentAvatarComponent";
import {AgentAvatarBody} from "./AgentAvatarBody";
import {AgentTrioComponent} from "../trio/AgentTrioComponent";
import {Resources} from "../../../../../../../resources/Resources";
import {SiteWhiteLabelAgent} from "../../../../../../../models/site/whitelabel/SiteWhiteLabelAgent";
import {AgentAvatarState} from "./types/AgentAvatarState";
import {AgentAvatarReaction} from "./types/AgentAvatarReaction";

/**
 * Le visage des agents dans tout le panel : chaque portrait d'Alya, Maya, Rémi, Nox, Marc ou Louise posé par un écran
 * (image ou icône marquée data-agent-face) devient l'avatar animé, à la taille qu'avait le portrait. Les illustrations
 * (équipe, scènes) et les visages d'une marque blanche restent des images.
 */
export class AgentFaceUpgrader {

    // tous les dessins d'un agent : portrait, navigation, bannière, scène Google, scène de réussite
    private static PORTRAIT = /\/agents\/agent-(?:(?:nav|bottom|google|win|steps)-)?(alya|maya|remi|nox|marc|louise|luca|jule|chris|max)(?:-[a-z]+)*\.png(?:\?.*)?$/;
    // l'équipe au complet devient le trio
    private static TEAM = /\/agents\/agent-(?:team(?:-[a-z]+)*|nav-multi(?:-[a-z]+)*)\.png(?:\?.*)?$/;
    private static FACE = /^(alya|maya|remi|nox|marc|louise|luca|jule|chris|max)(?=$|[A-Z])/;
    private static TEAM_FACE = /^team/i;
    private static KEEP = "data-agent-keep";
    private static SELECTOR = "img, [data-agent-face]";
    private static DONE = "data-agent-face-done";
    private static PERSONAS: { [key: string]: number } = {
        alya: CommunityAgentScope.PERSONA_ALYA,
        maya: CommunityAgentScope.PERSONA_MAYA,
        remi: CommunityAgentScope.PERSONA_REMI,
        nox: CommunityAgentScope.PERSONA_NOX,
        marc: CommunityAgentScope.PERSONA_MARC,
        louise: CommunityAgentScope.PERSONA_LOUISE,
        luca: CommunityAgentScope.PERSONA_LUCA,
        jule: CommunityAgentScope.PERSONA_JULE,
        chris: CommunityAgentScope.PERSONA_CHRIS,
        max: CommunityAgentScope.PERSONA_MAX
    };
    private static STATES: string[] = Object.values(AgentAvatarState);
    // part du cadre que remplit le corps de l'agent, comme le visage d'un portrait détouré
    private static FILL = 0.94;
    public static STATE_ATTRIBUTE = "data-agent-state";
    public static REACT_ATTRIBUTE = "data-agent-react";
    // un écran qui pilote lui-même ses agents coupe les réactions automatiques (clic, survol)
    public static QUIET_ATTRIBUTE = "data-agent-quiet";
    private static REACTIONS: string[] = Object.values(AgentAvatarReaction);
    // un clic ou un survol fait réagir les agents de l'élément touché, pas ceux de toute une page
    private static INTERACTION_DEPTH = 5;
    private static INTERACTION_MAX_FACES = 3;
    private static HOVER_COOLDOWN_MILLIS = 2500;
    private static REPLAY_MILLIS = 1200;
    private static avatars: WeakMap<Element, AgentAvatarComponent> = new WeakMap();
    // les avatars que ce remplacement a posés lui-même : aucun écran ne les détruit, il les libère quand leur
    // portrait quitte la page
    private static owners: WeakMap<Element, Component> = new WeakMap();
    // portraits posés dans un bloc encore masqué : ils attendent d'avoir une taille pour être remplacés
    private static hidden: Set<HTMLImageElement> = new Set();
    private static lastHover: WeakMap<Element, number> = new WeakMap();
    private static observer: MutationObserver;

    public static install(): void {
        const body = SedestralInterface.body;
        if (!body || body.isNull() || this.observer) {
            return;
        }
        this.scan(body.getHTMLElement());
        this.observer = new MutationObserver(records => this.onMutations(records));
        this.observer.observe(body.getHTMLElement(), {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: [this.STATE_ATTRIBUTE, this.REACT_ATTRIBUTE]
        });
        body.putListener(document, "click", (event: MouseEvent) => this.onInteraction(event, AgentAvatarReaction.HAPPY), true);
        body.putListener(document, "pointerover", (event: PointerEvent) => this.onInteraction(event, AgentAvatarReaction.CURIOUS), true);
    }

    /**
     * Un seul observateur : les portraits ajoutés deviennent des agents, ceux qui quittent la page libèrent leur
     * avatar, et les états et réactions posés par les écrans passent aux agents. Il ne suit ni les classes ni les
     * styles : les avatars en changent sans cesse, et un portrait masqué est suivi par sa propre taille.
     */
    private static onMutations(records: MutationRecord[]): void {
        const removed: Element[] = [];
        records.forEach(record => {
            if (record.type == "childList") {
                record.addedNodes.forEach(node => node instanceof HTMLElement && this.scan(node));
                record.removedNodes.forEach(node => node instanceof Element && removed.push(node));
            } else {
                this.onAttribute(record.target as Element, record.attributeName);
            }
        });
        removed.forEach(node => !node.isConnected && this.release(node));
        if (removed.length > 0) {
            this.forgetDetachedHidden();
        }
    }

    /**
     * Un avatar posé par un autre composant (chat, trio) rejoint les autres : il suit les états et réactions de son
     * écran comme eux.
     */
    public static register(holder: HTMLElement, avatar: AgentAvatarComponent): void {
        holder.setAttribute(this.DONE, "1");
        this.avatars.set(holder, avatar);
    }

    public static avatarsIn(root: Element): AgentAvatarComponent[] {
        return this.holdersIn(root).map(holder => this.avatars.get(holder)).filter(avatar => !!avatar && !avatar.isNull());
    }

    private static holdersIn(root: Element): Element[] {
        const holders: Element[] = root.hasAttribute(this.DONE) ? [root] : [];
        root.querySelectorAll(`[${this.DONE}]`).forEach(holder => holders.push(holder));
        return holders;
    }

    /**
     * Les avatars posés ici ne sont rattachés à aucun écran : sortis de la page, ils restaient gardés par le
     * registre des composants. Ils en sont retirés sans être vidés, de sorte qu'un bloc remis en page plus tard
     * garde son agent vivant.
     */
    private static release(root: Element): void {
        this.holdersIn(root).forEach(holder => {
            const owner = this.owners.get(holder);
            if (owner) {
                this.owners.delete(holder);
                this.forget(owner);
            }
        });
    }

    private static forget(component: Component): void {
        (component.childrens ?? []).forEach(child => this.forget(child));
        SedestralInterface.unStore(component);
    }

    /**
     * L'état d'un agent est celui que l'écran a posé sur lui ou sur l'un de ses conteneurs, le plus proche gagnant.
     */
    public static stateOf(holder: Element): AgentAvatarState {
        const state = holder.closest(`[${this.STATE_ATTRIBUTE}]`)?.getAttribute(this.STATE_ATTRIBUTE);
        return this.STATES.includes(state) ? state as AgentAvatarState : AgentAvatarState.IDLE;
    }

    /**
     * Un écran qui redessine son contenu juste après avoir fait réagir ses agents remplace l'avatar en pleine réaction :
     * le nouveau la reprend s'il naît dans la seconde qui suit.
     */
    private static recentReaction(holder: Element): AgentAvatarReaction {
        const [reaction, time] = (holder.closest(`[${this.REACT_ATTRIBUTE}]`)?.getAttribute(this.REACT_ATTRIBUTE) ?? "").split(":");
        return this.REACTIONS.includes(reaction) && Date.now() - Number(time) < this.REPLAY_MILLIS ? reaction as AgentAvatarReaction : undefined;
    }

    private static onAttribute(target: Element, attribute: string): void {
        if (attribute == this.STATE_ATTRIBUTE) {
            this.holdersIn(target).forEach(holder => {
                const avatar = this.avatars.get(holder);
                if (avatar && !avatar.isNull()) {
                    avatar.setState(this.stateOf(holder));
                }
            });
            return;
        }
        const reaction = (target.getAttribute(this.REACT_ATTRIBUTE) ?? "").split(":")[0];
        if (this.REACTIONS.includes(reaction)) {
            this.avatarsIn(target).forEach(avatar => avatar.react(reaction as AgentAvatarReaction));
        }
    }

    /**
     * Cliquer sur ce qui porte un agent (un bouton de menu, une carte) le rend content ; le survoler l'intrigue. Seuls
     * les agents de l'élément touché réagissent, et un même élément ne s'agite pas à chaque passage de la souris.
     * Le survol ne coupe pas une réaction déjà en cours (un salut, une fierté posés par l'écran).
     */
    private static onInteraction(event: Event, reaction: AgentAvatarReaction): void {
        let element = event.target instanceof Element ? event.target : undefined;
        if (!element || element.closest(`[${this.QUIET_ATTRIBUTE}]`)) {
            return;
        }
        for (let depth = 0; element && depth < this.INTERACTION_DEPTH; depth++, element = element.parentElement) {
            const avatars = this.avatarsIn(element);
            if (avatars.length == 0) {
                continue;
            }
            if (avatars.length > this.INTERACTION_MAX_FACES) {
                return;
            }
            if (reaction == AgentAvatarReaction.CURIOUS) {
                const last = this.lastHover.get(element) ?? 0;
                if (Date.now() - last < this.HOVER_COOLDOWN_MILLIS) {
                    return;
                }
                this.lastHover.set(element, Date.now());
            }
            avatars.filter(avatar => avatar.state == AgentAvatarState.IDLE && (reaction != AgentAvatarReaction.CURIOUS || !avatar.reaction))
                .forEach(avatar => avatar.react(reaction));
            return;
        }
    }

    private static scan(root: HTMLElement): void {
        const found: HTMLElement[] = root.matches(this.SELECTOR) ? [root] : [];
        root.querySelectorAll<HTMLElement>(this.SELECTOR).forEach(element => found.push(element));
        found.forEach(element => element instanceof HTMLImageElement ? this.upgradeImage(element) : this.upgradeIcon(element));
    }

    private static upgradeImage(image: HTMLImageElement, retried?: boolean): void {
        if (image.hasAttribute(this.DONE) || !image.isConnected || image.closest(`[${this.KEEP}]`)) {
            return;
        }
        const source = image.getAttribute("src");
        const team = !!source && this.TEAM.test(source) && !Resources.agentHasCustomImage(SiteWhiteLabelAgent.NAV_MULTI);
        const persona = team ? undefined : this.personaOfUrl(source);
        if (!persona && !team) {
            return;
        }
        const size = this.sizeOf(image);
        if (!size) {
            if (!retried && !image.complete) {
                const listener = SedestralInterface.body.putListener(image, "load", () => {
                    SedestralInterface.body.deleteListener(listener);
                    this.upgradeImage(image, true);
                });
            } else {
                this.waitForSize(image);
            }
            return;
        }
        this.stopWaiting(image);
        const holder = document.createElement("span");
        holder.className = image.className;
        holder.setAttribute(this.DONE, "1");
        if (image.hasAttribute(this.STATE_ATTRIBUTE)) {
            holder.setAttribute(this.STATE_ATTRIBUTE, image.getAttribute(this.STATE_ATTRIBUTE));
        }
        const placed = getComputedStyle(image);
        holder.style.cssText = `${image.style.cssText};margin:${placed.marginTop} ${placed.marginRight} ${placed.marginBottom} ${placed.marginLeft};`
            + `display:inline-flex;align-items:center;justify-content:center;`
            + `width:${size.width}px;max-width:100%;height:auto;aspect-ratio:${size.width} / ${size.height};`
            + "flex-shrink:0;overflow:visible;background:none;border:none;box-shadow:none;border-radius:0;vertical-align:middle;";
        image.replaceWith(holder);
        this.unframe(holder.parentElement);
        const square = this.squareInside(size);
        if (team) {
            const group = document.createElement("span");
            group.style.cssText = `display:block;${square}flex-shrink:0;pointer-events:none;`;
            holder.appendChild(group);
            const owner = SedestralInterface.el(group);
            owner.render(new AgentTrioComponent());
            this.owners.set(holder, owner);
            return;
        }
        this.mount(holder, persona, square);
    }

    private static upgradeIcon(element: HTMLElement): void {
        if (element.hasAttribute(this.DONE)) {
            return;
        }
        const face = element.getAttribute("data-agent-face") ?? "";
        const team = this.TEAM_FACE.test(face) && !Resources.agentHasCustomImage(SiteWhiteLabelAgent.NAV_MULTI);
        const persona = team ? undefined : this.personaOfFace(face);
        if (!team && (!persona || CommunityAgentScope.personaHasCustomImage(persona))) {
            return;
        }
        element.setAttribute(this.DONE, "1");
        this.unframe(element, true);
        element.style.backgroundImage = "none";
        element.style.webkitMaskImage = "none";
        element.style.maskImage = "none";
        element.style.filter = "none";
        element.style.overflow = "visible";
        element.style.display = getComputedStyle(element).display == "inline" ? "inline-flex" : element.style.display;
        element.style.alignItems = "center";
        element.style.justifyContent = "center";
        if (team) {
            const group = document.createElement("span");
            group.style.cssText = "display:block;width:100%;height:100%;aspect-ratio:1;pointer-events:none;";
            element.appendChild(group);
            const owner = SedestralInterface.el(group);
            owner.render(new AgentTrioComponent());
            this.owners.set(element, owner);
            return;
        }
        this.mount(element, persona, "width:100%;height:100%;");
    }

    /**
     * Les écrans nomment aussi les variantes d'un agent (marcBig, mayaBottomMid, remiGoogle, alyaWin) : c'est
     * toujours le même agent.
     */
    private static personaOfFace(face: string): number {
        const match = this.FACE.exec(face.charAt(0).toLowerCase() + face.slice(1));
        return match ? this.PERSONAS[match[1]] : undefined;
    }

    /**
     * Le corps de l'agent remplit la même part du cadre quelle que soit sa forme, centré, à la façon d'un portrait.
     */
    private static mount(holder: HTMLElement, persona: number, square: string): void {
        const fit = AgentAvatarBody.fit(AgentAvatarBody.identity(persona), this.FILL);
        const face = document.createElement("span");
        face.style.cssText = `display:block;${square}max-width:100%;max-height:100%;aspect-ratio:1;pointer-events:none;`
            + `flex-shrink:0;transform:translate(${(fit.shiftX * 100).toFixed(1)}%,${(fit.shiftY * 100).toFixed(1)}%)`
            + ` scale(${fit.scale.toFixed(3)});`;
        holder.appendChild(face);
        const avatar = new AgentAvatarComponent({persona: persona, state: this.stateOf(holder), reaction: this.recentReaction(holder)});
        const owner = SedestralInterface.el(face);
        owner.render(avatar);
        this.avatars.set(holder, avatar);
        this.owners.set(holder, owner);
    }

    /**
     * Le cadre rond qui entourait un portrait (pastille de fond, bordure, ombre, découpe en cercle) n'a plus de sens
     * autour d'un agent sans fond : il disparaît, que ce soit l'élément lui-même ou le conteneur qui n'entoure que lui.
     */
    private static unframe(element: HTMLElement, self?: boolean): void {
        if (!element || element == document.body || (!self && element.children.length != 1)) {
            return;
        }
        const style = getComputedStyle(element);
        const radius = parseFloat(style.borderTopLeftRadius) || 0;
        const round = style.borderTopLeftRadius.endsWith("%") ? radius >= 30 : radius >= element.getBoundingClientRect().width * .3;
        if (!round) {
            return;
        }
        element.style.border = "none";
        element.style.boxShadow = "none";
        element.style.outline = "none";
        element.style.backgroundColor = "transparent";
        element.style.overflow = "visible";
        element.style.borderRadius = "0";
    }

    private static personaOfUrl(url: string): number {
        const match = url ? this.PORTRAIT.exec(url) : null;
        if (!match) {
            return undefined;
        }
        const persona = this.PERSONAS[match[1]];
        return CommunityAgentScope.personaHasCustomImage(persona) ? undefined : persona;
    }

    /**
     * La taille que l'écran donne au portrait, sans les transformations en cours : une fenêtre qui s'ouvre en
     * grandissant (scaleShow part de 90 %) ne doit pas laisser un agent rétréci. Masqué (bloc replié, onglet
     * caché), le portrait n'a pas encore de taille : sa taille naturelle de fichier ferait un agent démesuré, il
     * attend donc d'être affiché.
     */
    private static sizeOf(image: HTMLImageElement): { width: number, height: number } {
        if (image.offsetWidth > 0 && image.offsetHeight > 0) {
            return {width: image.offsetWidth, height: image.offsetHeight};
        }
        if (image.getClientRects().length == 0) {
            return undefined;
        }
        const style = getComputedStyle(image);
        const width = parseFloat(style.width);
        const height = parseFloat(style.height);
        return width > 0 ? {width: width, height: height > 0 ? height : width} : undefined;
    }

    /**
     * Le carré de l'agent, centré dans le cadre du portrait selon son côté le plus court, en proportion du cadre et
     * non en pixels, pour suivre le cadre quand il change de taille.
     */
    private static squareInside(size: { width: number, height: number }): string {
        return size.width >= size.height ? "width:auto;height:100%;" : "width:100%;height:auto;";
    }

    /**
     * Un portrait masqué est remplacé dès qu'il prend une taille, sans relire la page à chaque changement.
     */
    private static waitForSize(image: HTMLImageElement): void {
        if (this.hidden.has(image)) {
            return;
        }
        this.hidden.add(image);
        image["resizeFunctions"] = [() => image.offsetWidth > 0 && this.upgradeImage(image, true)];
        SedestralMachine.resizeObserver.observe(image);
    }

    private static stopWaiting(image: HTMLImageElement): void {
        if (this.hidden.delete(image)) {
            SedestralMachine.resizeObserver.unobserve(image);
        }
    }

    private static forgetDetachedHidden(): void {
        Array.from(this.hidden).filter(image => !image.isConnected).forEach(image => this.stopWaiting(image));
    }
}
